import React, { useEffect, useState } from 'react';
import partnerApi from '../../api/partnerApi';
import { Search, ExternalLink } from 'lucide-react';

const STATUS_CONFIG = {
  visitor:    { label: 'Clicked',    cls: 'bg-slate-100 text-slate-600' },
  registered: { label: 'Registered', cls: 'bg-blue-100 text-blue-700' },
  trial:      { label: 'Trial',      cls: 'bg-amber-100 text-amber-700' },
  subscribed: { label: 'Paying',     cls: 'bg-emerald-100 text-emerald-700' },
};

export default function PartnerReferrals() {
  const [referrals, setReferrals] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const LIMIT = 20;

  const load = (p = 1) => {
    setLoading(true);
    partnerApi.get(`/referrals?page=${p}&limit=${LIMIT}`)
      .then((res) => {
        if (res?.success) {
          setReferrals(res.data?.referrals || []);
          setTotal(res.data?.total || 0);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1); }, []);

  const filtered = filter
    ? referrals.filter(r =>
        r.school?.name?.toLowerCase().includes(filter.toLowerCase()) ||
        r.status?.includes(filter) ||
        r.visitor_id?.includes(filter)
      )
    : referrals;

  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-outfit font-semibold text-slate-900">My Referrals</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          {total} total referral{total !== 1 ? 's' : ''} tracked
        </p>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter referrals..."
            className="w-full pl-9 border border-slate-200 rounded-lg py-2 text-sm focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA] outline-none"
            data-testid="partner-referrals-filter"
          />
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden" data-testid="partner-referrals-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">School / Visitor</th>
              <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Status</th>
              <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Source</th>
              <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Date</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={4} className="px-5 py-8 text-center text-slate-400 text-sm">Loading...</td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="px-5 py-8 text-center text-slate-400 text-sm">
                  No referrals yet. Share your link to get started!
                </td>
              </tr>
            )}
            {filtered.map((r) => {
              const st = STATUS_CONFIG[r.status] || { label: r.status, cls: 'bg-slate-100 text-slate-600' };
              const dateStr = r.created_at ? new Date(r.created_at).toLocaleDateString() : '—';
              const schoolName = r.school?.name || '—';
              return (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                  <td className="px-5 py-3">
                    <p className="font-medium text-slate-800 text-sm">{schoolName}</p>
                    {r.school?.city && (
                      <p className="text-xs text-slate-400">{r.school.city}</p>
                    )}
                    {!r.school_id && (
                      <p className="text-xs text-slate-400 font-mono">{r.visitor_id?.slice(0, 8)}...</p>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500">
                    {r.utm_source || r.landing_page || '—'}
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500">{dateStr}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {pages > 1 && (
          <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
            <p className="text-xs text-slate-500">Page {page} of {pages}</p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => { setPage(p => p - 1); load(page - 1); }}
                className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50 transition"
              >Prev</button>
              <button
                disabled={page >= pages}
                onClick={() => { setPage(p => p + 1); load(page + 1); }}
                className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50 transition"
              >Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
